/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import _ from 'lodash'
import { InputSelect } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'

import Actions from '../../../../actions'
import Operations from '../../../../operations'
import { getObjectDiff } from '../../../../common/utils'
import Selectors from '../../../../selectors'
import { history } from '../../../../store'
import { withRouter } from '../../../../withRouter'
import { showToast } from '../../../../common/utils'

import Form from '../../../../components/forms'
import { Spinner } from '../../../../components/generic'
import {
  RiskAssessmentsSection,
  CarePlanDocuments,
} from '../../../../components/carePlans'
import SignatureSection from '../../components/CarePlanSignatures/CarePlanSignatures'

import Header, {
  ICarePlanForm,
} from './components/CarePlanReviewHeader/CarePlanReviewHeader'
import Section from './components/CarePlanReviewSection/CarePlanReviewSection'
import Footer from './components/CarePlanReviewFooter/CarePlanReviewFooter'
import NextReview from './components/CarePlanReviewNextReview/CarePlanReviewNextReview'
import styles from './CarePlanReiviewForm.styles'
// we need full path here in order to make jest.mock work
import {
  Carer,
  Document,
  CarePlan,
  Resident,
  LogCategoryItem,
  RiskAssessment,
  Log,
  Home,
} from '@lmc/ts-common'
import { Form as AntdForm } from 'antd'
import { ProfileNeedOption } from 'screens/Residents/screens/ResidentNeedsScreen/ResidentNeedsScreen'
import useSkillsAndNeedsTemplate, {
  NeedsAndSkills,
} from 'hooks/useSkillsAndNeedsTemplate'
import CarePlanGoalsListInput from '../CarePlanViewScreen/components/CarePlansGoalsListInput/CarePlanGoalsListInput'
import useGoalsAccess from 'components/AccessControl/features/useGoalsAccess'
import { CarePlanSectionContainer } from '../../components/CarePlanSections'
import { CAREPLAN_ADL_PBS_ID } from '@lmc/common/types'
import {
  getFormSectionsInitialValue,
  getSectionsToSave,
  getDefaultPBSSections,
} from './utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import NavigationModal from 'components/NavigationModal/NavigationModal'

type SetFormField = {
  key: string
  value: string | number | string[] | boolean
}

type UpdateItem = {
  url: string
  json: CarePlan
  onComplete: () => void
  onError: (e: Error) => void
}

interface CarePlanFormType {
  carers: Carer[]
  carePlanNominative: string
  deleteDocument: (id: string, cb: () => void) => void
  documents: Document
  errors?: string[]
  initial: CarePlan
  isSubmitting: boolean
  fields: CarePlan
  openDocumentModal: (doc: { type: string; document?: Document }) => void
  openRiskAssessmentModal: (doc: RiskAssessment) => void
  params: { id: string; care_plan_id: string }
  riskAssessments?: RiskAssessment[]
  resident: Resident
  router: any
  setFormField: (a: SetFormField) => void
  setLoadedListItem: (update: { listId: string; item: any }) => void
  signatureLogs?: Log[]
  signatureItem?: LogCategoryItem
  updateItem: (update: UpdateItem) => void
  profileNeedOptions: ProfileNeedOption[]
  features: Home
  fetchNeedsOptions: () => void
  goals: string[]
}

export const CarePlanReviewForm = ({
  carers,
  carePlanNominative,
  deleteDocument,
  documents,
  errors,
  initial,
  isSubmitting,
  fields,
  openDocumentModal,
  openRiskAssessmentModal,
  params: { id, care_plan_id: carePlanId },
  riskAssessments,
  resident,
  router,
  setFormField,
  setLoadedListItem,
  signatureLogs,
  signatureItem,
  updateItem,
  profileNeedOptions,
  features,
  fetchNeedsOptions,
}: CarePlanFormType): React.ReactElement => {
  const { t } = useTranslation()
  const hasGoals = useGoalsAccess()

  const [success, setSuccess] = useState(false)

  // Prevent accidentally navigating away, loosing unsaved work
  const isDirty = !_.isEqual(fields, initial) // Is this too heavy?

  const pbsFlagEnabled = useFeatureFlags({ flagNames: ['pbs'] })?.data.pbs
    .enabled

  const isPBSAssessment =
    pbsFlagEnabled && fields?.carePlanAdl?.id === CAREPLAN_ADL_PBS_ID

  const url = `/residents/${id}/care-plans/${carePlanId}`

  const handleCancel = () => history.push(url)

  const [form] = AntdForm.useForm()

  const [formPBS] = AntdForm.useForm()

  const canNavigate = !isDirty || success

  const submitSectionsForm = async () => {
    let formResult
    try {
      await formPBS.validateFields()
      formResult = formPBS.getFieldsValue()
    } catch (e) {
      formResult = false
    }
    return formResult
  }

  const { textNode, linkNode } = useSkillsAndNeedsTemplate({
    type: NeedsAndSkills.needs,
    elements: [],
  })

  const handleSubmit = () => {
    if (isDirty && !fields.noFurtherSupportRequired && !fields.nextReviewDate) {
      showToast({
        title: 'Next review date needs to be specified',
        type: 'warning',
      })
      return
    }

    if (isDirty || formPBS?.isFieldsTouched()) {
      const changes = getObjectDiff(fields, initial)

      // On submit, reviewComment is set to newComment,
      // and newComment is set to null. This makes sure
      // that if no comment is entered on review, the
      // default value of comment is null.
      //
      // If newComment is undefined, reviewComment is still set to null.
      changes.reviewComment = changes.newComment ? changes.newComment : null
      delete changes.newComment

      setSuccess(true)

      const url = `list/care-plans/${fields.id}`

      const baseJson = {
        ...changes,
        carePlanId: fields.carePlanId,
        reviewedAssessments: undefined,
        // @ts-ignore
        seenBy: null,
      }

      if (isPBSAssessment) {
        const sectionsFormResult = submitSectionsForm()

        sectionsFormResult.then((sections) => {
          const sectionsToSave = getSectionsToSave(fields, sections)

          if (sections) {
            updateItem({
              url,
              json: {
                ...baseJson,
                sections: sectionsToSave,
              } as unknown as CarePlan,
              onComplete: handleComplete,
              onError: (e: Error) => {
                // eslint-disable-next-line no-console
                console.error(e)
              },
            })
          }
        })
        return
      }

      updateItem({
        url: `list/care-plans/${fields.id}`,
        json: baseJson as unknown as CarePlan,
        onComplete: handleComplete,
        onError: (e: Error) => {
          // eslint-disable-next-line no-console
          console.error(e)
        },
      })
    } else {
      handleCancel()
    }
  }

  const handleComplete = () => {
    showToast({ title: 'Reviewed a Care Plan' })

    history.push(`/residents/${id}/care-plans/${carePlanId}`)
  }

  const handleDeleteDocument = (documentId: string) => {
    showToast({ type: 'info', title: 'Deleted a Care Plan Document' })
    deleteDocument(documentId, () => {
      setFormField({
        key: 'documents',
        value: _.without(fields.documents, id) as
          | string
          | number
          | boolean
          | string[],
      })
    })
  }

  useEffect(() => {
    form.setFieldsValue({ skillNeeds: fields?.skillNeeds })
  }, [fields?.skillNeeds])

  useEffect(() => {
    if (features?.features?.skillsAndNeeds?.enabled) {
      fetchNeedsOptions()
    }
  }, [])

  useEffect(() => {
    if (isPBSAssessment && !isDirty) {
      formPBS.setFieldsValue(getFormSectionsInitialValue(fields))
    }
  }, [fields?.sections])

  const hasInitialPBSSections = fields?.sections?.length

  const defaultPBSSections = getDefaultPBSSections(fields)

  if (!fields || !carers || !documents || !resident || !router) {
    return <Spinner />
  }

  return (
    <div style={styles.wrapper}>
      <NavigationModal canNavigate={canNavigate} />
      <Header
        fields={fields as ICarePlanForm}
        carers={carers}
        errors={errors || []}
        signatureLogs={signatureLogs}
        resident={resident}
        setFormField={setFormField}
        carePlanNominative={carePlanNominative}
        handleCancel={handleCancel}
        FORM_ID={FORM_ID}
      />
      {isPBSAssessment && (
        <CarePlanSectionContainer
          sections={
            hasInitialPBSSections ? fields?.sections : defaultPBSSections
          }
          form={formPBS}
        />
      )}
      {!isPBSAssessment && (
        <Section
          fields={fields}
          title="Strengths"
          FORM_ID={FORM_ID}
          content={fields.strengths}
        />
      )}
      {!isPBSAssessment && (
        <Section
          fields={fields}
          FORM_ID={FORM_ID}
          title="Needs"
          content={fields.needs}
          innerComponent={
            features?.features?.skillsAndNeeds?.enabled &&
            profileNeedOptions?.length > 0 ? (
              <AntdForm form={form}>
                <div style={styles.needItem}>
                  <InputSelect
                    type="skillNeeds"
                    label={t(`skillsAndNeeds.selectNeeds`)}
                    layout="row"
                    placeholder={t(`skillsAndNeeds.chooseFormList`)}
                    options={profileNeedOptions?.map((needOption) => ({
                      label: needOption.name,
                      options: needOption.needs?.map((need) => ({
                        label: need.name,
                        value: need.id,
                      })),
                    }))}
                    mode="multiple"
                    style={styles.inputSelect}
                    formItemstyle={styles.formInputSelectWrapper}
                    onChange={(value) =>
                      setFormField({ key: 'skillNeeds', value })
                    }
                  />
                </div>
              </AntdForm>
            ) : (
              <div style={{ paddingTop: 17, marginLeft: 24 }}>
                {textNode}
                {linkNode}
              </div>
            )
          }
        />
      )}
      <RiskAssessmentsSection
        carers={carers}
        fields={fields}
        riskAssessments={riskAssessments}
        router={router}
        setFormField={setFormField}
        onOpenModal={openRiskAssessmentModal}
        showToast={showToast}
      />
      {hasGoals && (
        <CarePlanGoalsListInput
          onChange={(value) =>
            setFormField({
              key: 'goals',
              value,
            })
          }
          disabled={false}
          value={(fields.goals as string[]) || []}
        />
      )}
      <div style={styles.sectionContainer}>
        <CarePlanDocuments
          carePlan={fields}
          documents={documents}
          onCreate={() => openDocumentModal({ type: 'create' })}
          onEdit={(document: any) =>
            openDocumentModal({ document, type: 'edit' })
          }
          onDelete={handleDeleteDocument}
        />
      </div>
      <NextReview
        fields={fields}
        FORM_ID={FORM_ID}
        setFormField={setFormField}
      />
      <SignatureSection
        carePlan={fields}
        logs={signatureLogs}
        resident={resident}
        setLoadedListItem={setLoadedListItem}
        preferredCarePlanName={carePlanNominative}
        item={signatureItem?.id}
        category={(signatureItem as LogCategoryItem)?.category as string}
      />
      <Footer
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  )
}

const FORM_ID = 'editCarePlan'
const DOCUMENT_MODAL_ID = 'carePlanDocument'
const RISK_ASSESSMENT_MODAL_ID = 'createRiskAssessment'

const mapStateToProps = (state: any, props: any) => {
  const { id } = props.params
  return {
    signatureItem: _.find(state.data['log-category-items'], {
      itemId: 'signature',
    }),
    signatureLogs: Selectors.getCarePlanLogs(state, props),
    riskAssessments: _.filter(state.data['risk-assessments'], { resident: id }),
    carers: state.data.carers,
    documents: _.filter(state.data.documents, { resident: id }),
    userId: state.global.user.id,
    resident: _.find(state.data.residents, { id }),
    carePlanNominative: state.global.home.preferredCarePlansNominative,
    profileNeedOptions: state.data['skill-need-templates/categories'],
    features: state.global.home,
    goals: state.forms?.editCarePlan?.fields.goals,
  }
}

const mapDispatchToProps = (dispatch: any, props: any) => ({
  deleteDocument: (id: unknown, onComplete: any) =>
    dispatch(
      Operations.deleteFormItem({
        id,
        list: 'documents',
        onComplete,
      })
    ),
  setLoadedListItem: (listId: unknown, item: unknown) =>
    dispatch(Actions.setLoadedListItem(listId as string, item as object)),
  // @ts-ignore
  openDocumentModal: ({ document, type }) => {
    dispatch(
      Actions.openModal({
        modalId: DOCUMENT_MODAL_ID,
        data: { type, parentFormId: FORM_ID },
      })
    )
    dispatch(
      Actions.addForm({
        fields: { resident: props.router.params.id } as any,
        formId: DOCUMENT_MODAL_ID,
        initial: document,
      })
    )
  },
  openRiskAssessmentModal: (completedRiskAssessment: any) => {
    dispatch(
      Actions.openModal({
        modalId: RISK_ASSESSMENT_MODAL_ID,
        data: {
          completedRiskAssessment,
          parentFormId: FORM_ID,
          attachToCarePlan: props.fields,
        },
      })
    )
  },
  fetchNeedsOptions: () =>
    dispatch(
      Operations.loadList('skill-need-templates/categories', { needs: 1 })
    ),
})

const wrappedForm = withRouter(
  // @ts-ignore
  connect(mapStateToProps, mapDispatchToProps)(CarePlanReviewForm)
)

export default Form(wrappedForm, FORM_ID)
